// Gonzaga University: public college page (#2839), official facts only.
//
// gonzaga.edu sits behind a Cloudflare challenge that plain requests do not
// pass, so every gonzaga.edu page below was read in a headless browser that
// waited out the challenge; the Common Data Set PDF was saved through that
// browser. Every figure below was read on 2026-10-01 from the source it
// names:
//  - Gonzaga's Common Data Set 2025-2026 (Institutional Research, linked
//    from its Reports page; the newest edition posted), read from the
//    rendered PDF pages because its figures are not in the text layer: C1
//    applicants, admits and enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027, C21/C22 (no early decision; nonbinding,
//    not restrictive early action closing November 15, with no counts),
//    G1/G5 2026-2027 costs. Quirk: the C1 table by sex counts 1,137
//    enrollees (538 men, 592 women, 7 unknown sex), while the enrollees by
//    status (538 full-time men, 592 full-time women, no unknown) and the
//    residency table both total 1,130; the page uses 1,130. Its G1 food and
//    housing figure ($17,135) is $25 under housing only plus food only
//    ($8,940 + $8,220); the page uses the G1 food and housing line.
//  - gonzaga.edu Undergraduate Admission: Dates & Deadlines (the first-year
//    table; it carries no year, so it is read as the deadlines for the
//    application now open, fall 2027 entry), Admission Requirements (test
//    policy, nursing), First-Year Students (application checklist, $60 fee,
//    nursing short answer).
//  - College Scorecard API, school id 235316 (net price by family income).
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Gonzaga Common Data Set 2025-2026',
        url: 'https://www.gonzaga.edu/__data/assets/file/0022/217471/Gonzaga-University-CDS-2025-2026.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Gonzaga Undergraduate Admission, Dates & Deadlines',
        url: 'https://www.gonzaga.edu/undergraduate-admission/apply/dates-deadlines',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Gonzaga Undergraduate Admission, Admission Requirements',
        url: 'https://www.gonzaga.edu/undergraduate-admission/apply/admission-requirements',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Gonzaga Undergraduate Admission, First-Year Students',
        url: 'https://www.gonzaga.edu/undergraduate-admission/apply/how-to-apply/first-year-students',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?235316-Gonzaga-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'gonzaga-university',
    kbId: 'gonzaga_university',
    name: 'Gonzaga University',
    shortName: 'Gonzaga',
    aliases: ['GU', 'Zags'],
    location: 'Spokane, WA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Gonzaga trademarks and logos page); decoration only.
    branding: {
        primary: '#041E42',
        secondary: '#C8102E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/gonzaga_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8906,
        admitted: 7091,
        enrolled: 1130,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3342, admitted: 2606 },
            { group: 'Out-of-state', applied: 5301, admitted: 4349 },
            { group: 'International', applied: 263, admitted: 136 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Gonzaga\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Gonzaga has no early decision plan. Its Early Action plan, closing November 15, is nonbinding and not restrictive. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Gonzaga through the Common App. Nursing works differently:',
        rows: [
            { school: 'Nursing', requirement: 'Direct admission for first-year applicants only: apply by the Early Action deadline of November 15 and choose nursing as your major on the application, with an extra nursing short answer question. Applications after November 15 are considered for the nursing waitlist. Admission is competitive, and you cannot change your major to nursing after you enroll.', source: 'firstYear' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'Late December', binding: false },
            { plan: 'Nursing', deadline: 'November 15', decision: 'Late December', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Early March', binding: false },
        ],
        note: 'The confirmation deadline is May 1. The financial aid priority date is December 1 for Early Action and February 1 for Regular Decision. The application fee is $60, and a Common App fee waiver is accepted.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Gonzaga does not require the SAT or ACT, and applicants without a score have the same access to admission, automatic merit scholarships, direct admission to nursing and the Honors Program. Two exceptions: homeschooled students with fewer than 30 semester or 45 quarter hours of college coursework must send an SAT or ACT, and international students must show English proficiency with a test score. If you send scores, Gonzaga combines your best section scores across test dates.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, with scores due by February 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 58390 },
            { item: 'Required fees', amount: 1120 },
            { item: 'Food and housing (on campus)', amount: 17135 },
            { item: 'Books and supplies', amount: 1188 },
            { item: 'Transportation', amount: 2796 },
            { item: 'Other expenses', amount: 2035 },
        ],
        total: 82664,
        note: 'For a full-time undergraduate living on campus, fall and spring. Gonzaga\'s tuition and fees do not vary by year or program.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 32750,
            byIncome: [
                { income: '$0 to $30,000', amount: 24657 },
                { income: '$30,001 to $48,000', amount: 21926 },
                { income: '$48,001 to $75,000', amount: 23254 },
                { income: '$75,001 to $110,000', amount: 28852 },
                { income: 'Over $110,000', amount: 41049 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Gonzaga\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
